import React from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Building2, LogOut, Home } from "lucide-react";

/**
 * Layout is context-aware.
 * Props:
 *  - navItems: [{ to?, onClick?, label, icon, testid, active?, exact? }]
 *  - sidebarExtra: JSX rendered inside sidebar under nav
 *  - sidebarHeader: JSX rendered inside sidebar above nav (e.g. property name)
 * If navItems is undefined, the default = minimal "Properties" only.
 */
export default function Layout({ children, title, subtitle, actions, navItems, sidebarExtra, sidebarHeader }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const items =
    navItems ||
    [{ to: "/", label: "Properties", icon: Building2, testid: "nav-properties", exact: true }];

  return (
    <div className="min-h-screen flex bg-[#F8FAFC] text-slate-900">
      {/* Sidebar */}
      <aside className="w-64 border-r border-slate-200 bg-white flex flex-col" data-testid="sidebar">
        <Link to="/" className="px-6 py-6 border-b border-slate-200 block hover:bg-slate-50 transition-colors" data-testid="sidebar-logo">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-slate-900 grid place-items-center">
              <Home className="w-4.5 h-4.5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display font-bold text-lg tracking-tight leading-none">RoostHQ</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">Rental Operating System</div>
            </div>
          </div>
        </Link>

        {sidebarHeader}

        <nav className="flex-1 p-3 space-y-0.5">
          {items.map((n, i) => {
            const inner = (
              <>
                <n.icon className="w-4 h-4" strokeWidth={2} />
                <span className="font-medium">{n.label}</span>
              </>
            );
            const activeCls = "bg-slate-900 text-white";
            const idleCls = "text-slate-600 hover:bg-slate-100 hover:text-slate-900";
            const cls = `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors duration-200`;

            // Explicit active flag wins
            if (typeof n.active === "boolean") {
              return (
                <button
                  key={n.testid || i}
                  onClick={n.onClick}
                  data-testid={n.testid}
                  className={`${cls} w-full text-left ${n.active ? activeCls : idleCls}`}
                >
                  {inner}
                </button>
              );
            }
            if (n.onClick && !n.to) {
              return (
                <button key={n.testid || i} onClick={n.onClick} data-testid={n.testid} className={`${cls} w-full text-left ${idleCls}`}>
                  {inner}
                </button>
              );
            }
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.exact}
                data-testid={n.testid}
                className={({ isActive }) => `${cls} ${isActive ? activeCls : idleCls}`}
              >
                {inner}
              </NavLink>
            );
          })}

          {sidebarExtra && <div className="pt-2">{sidebarExtra}</div>}
        </nav>

        <div className="p-3 border-t border-slate-200">
          <div className="px-3 py-2 mb-2">
            <div className="text-xs text-slate-400 uppercase tracking-widest">Signed in as</div>
            <div className="text-sm font-medium text-slate-900 truncate" data-testid="current-user-name">{user?.name || user?.email}</div>
            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
          </div>
          <button
            onClick={async () => { await logout(); navigate("/login"); }}
            data-testid="logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors duration-200"
          >
            <LogOut className="w-4 h-4" />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0">
        <header className="px-8 py-6 border-b border-slate-200 bg-white/70 backdrop-blur-sm sticky top-0 z-20">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight" data-testid="page-title">{title}</h1>
              {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
            </div>
            {actions && <div className="flex items-center gap-2">{actions}</div>}
          </div>
        </header>
        <div className="p-8">{children}</div>
      </main>
    </div>
  );
}
